import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Plus, Edit, Trash2, BookOpen, Eye, EyeOff, Sparkles, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { coursesAPI, modulesAPI, quizzesAPI, aiAPI } from '../../lib/api';
import { toast } from 'sonner';

const categories = ['Technology', 'Business', 'Leadership', 'Communication', 'Design', 'Marketing', 'Finance', 'Data Science'];
const difficulties = ['beginner', 'intermediate', 'advanced'];

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [view, setView] = useState('list');
  const [form, setForm] = useState({ title: '', description: '', thumbnail_url: '', category: 'Technology', difficulty: 'beginner', duration_hours: 1 });
  const [moduleForm, setModuleForm] = useState({ title: '', description: '', order: 1, video_url: '', content: '', duration_minutes: 0 });
  const [selectedModule, setSelectedModule] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { fetchCourses(); }, []);

  const fetchCourses = async () => {
    try { const res = await coursesAPI.getAllAdmin(); setCourses(res.data); } catch { toast.error('Failed'); }
  };

  const fetchModules = async (id) => {
    try { const res = await modulesAPI.getByCourse(id); setModules(res.data); } catch { toast.error('Failed'); }
  };

  const selectCourse = async (c) => { setSelectedCourse(c); await fetchModules(c.id); setView('detail'); };

  const handleSaveCourse = async () => {
    try {
      if (selectedCourse) { await coursesAPI.update(selectedCourse.id, form); toast.success('Updated!'); }
      else { await coursesAPI.create(form); toast.success('Created!'); }
      fetchCourses(); setView('list'); setSelectedCourse(null);
    } catch { toast.error('Failed'); }
  };

  const handleSaveModule = async () => {
    try {
      await modulesAPI.create({ ...moduleForm, course_id: selectedCourse.id });
      toast.success('Created!'); fetchModules(selectedCourse.id); setView('detail');
    } catch { toast.error('Failed'); }
  };

  const handlePublish = async (id) => {
    try { await coursesAPI.publish(id); toast.success('Published!'); fetchCourses(); } catch { toast.error('Failed'); }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await coursesAPI.delete(id); toast.success('Deleted!'); fetchCourses(); setSelectedCourse(null); setView('list'); } catch { toast.error('Failed'); }
  };

  const handleGenQuiz = async () => {
    if (!selectedModule) return;
    setGenerating(true);
    try {
      const res = await aiAPI.generateQuiz({ topic: selectedModule.title, difficulty: 'medium', num_questions: 5 });
      setQuizQuestions(res.data.questions); toast.success('Generated!');
    } catch { toast.error('AI generation failed'); }
    finally { setGenerating(false); }
  };

  const handleSaveQuiz = async () => {
    if (!selectedModule || quizQuestions.length === 0) return;
    try {
      await quizzesAPI.create({ module_id: selectedModule.id, title: selectedModule.title + ' Quiz', description: '', passing_score: 70, time_limit_minutes: 30, questions: quizQuestions });
      toast.success('Quiz created!'); fetchModules(selectedCourse.id); setView('detail'); setQuizQuestions([]);
    } catch { toast.error('Failed'); }
  };

  if (view === 'form') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <h1 className="font-sketch text-4xl">{selectedCourse ? 'Edit Course' : 'New Course'}</h1>
          <div className="card-sketchy space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="input-sketchy" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input-sketchy" /></div>
            <div><Label>Thumbnail URL</Label><Input value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} className="input-sketchy" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label><Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger className="input-sketchy"><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Difficulty</Label><Select value={form.difficulty} onValueChange={v => setForm({ ...form, difficulty: v })}><SelectTrigger className="input-sketchy"><SelectValue /></SelectTrigger><SelectContent>{difficulties.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Duration (hours)</Label><Input type="number" value={form.duration_hours} onChange={e => setForm({ ...form, duration_hours: parseFloat(e.target.value) })} className="input-sketchy" /></div>
            <div className="flex gap-4">
              <Button className="btn-sketchy flex-1" onClick={handleSaveCourse}><Save className="w-4 h-4 mr-2" />Save</Button>
              <Button variant="outline" className="border-2 border-black" onClick={() => setView('list')}>Cancel</Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (view === 'module-form') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <h1 className="font-sketch text-4xl">Add Module</h1>
          <div className="card-sketchy space-y-4">
            <div><Label>Title</Label><Input value={moduleForm.title} onChange={e => setModuleForm({ ...moduleForm, title: e.target.value })} className="input-sketchy" /></div>
            <div><Label>Description</Label><Textarea value={moduleForm.description} onChange={e => setModuleForm({ ...moduleForm, description: e.target.value })} className="input-sketchy" /></div>
            <div><Label>Video URL</Label><Input value={moduleForm.video_url} onChange={e => setModuleForm({ ...moduleForm, video_url: e.target.value })} className="input-sketchy" /></div>
            <div><Label>Content</Label><Textarea value={moduleForm.content} onChange={e => setModuleForm({ ...moduleForm, content: e.target.value })} className="input-sketchy min-h-[100px]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Order</Label><Input type="number" value={moduleForm.order} onChange={e => setModuleForm({ ...moduleForm, order: parseInt(e.target.value) })} className="input-sketchy" /></div>
              <div><Label>Duration (min)</Label><Input type="number" value={moduleForm.duration_minutes} onChange={e => setModuleForm({ ...moduleForm, duration_minutes: parseInt(e.target.value) })} className="input-sketchy" /></div>
            </div>
            <div className="flex gap-4">
              <Button className="btn-sketchy flex-1" onClick={handleSaveModule}><Save className="w-4 h-4 mr-2" />Save</Button>
              <Button variant="outline" className="border-2 border-black" onClick={() => setView('detail')}>Cancel</Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (view === 'quiz-form') {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <h1 className="font-sketch text-4xl">Create Quiz: {selectedModule?.title}</h1>
          <div className="card-sketchy space-y-4">
            <div className="flex justify-between items-center">
              <p>{quizQuestions.length} questions</p>
              <Button variant="outline" className="border-2 border-primary text-primary" onClick={handleGenQuiz} disabled={generating}><Sparkles className="w-4 h-4 mr-2" />{generating ? 'Generating...' : 'AI Generate'}</Button>
            </div>
            {quizQuestions.map((q, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg border-2 border-black">
                <p className="font-semibold">Q{idx + 1}: {q.question}</p>
                {q.options.map((opt, i) => <p key={i} className={i === q.correct_answer ? 'text-green-600 font-bold' : ''}>{String.fromCharCode(65 + i)}. {opt}</p>)}
              </div>
            ))}
            <div className="flex gap-4">
              <Button className="btn-sketchy flex-1" onClick={handleSaveQuiz} disabled={quizQuestions.length === 0}><Save className="w-4 h-4 mr-2" />Save Quiz</Button>
              <Button variant="outline" className="border-2 border-black" onClick={() => { setView('detail'); setQuizQuestions([]); }}>Cancel</Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (view === 'detail' && selectedCourse) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="font-sketch text-4xl">{selectedCourse.title}</h1>
              <p className="text-muted-foreground">{selectedCourse.description}</p>
              <div className="flex gap-2 mt-2">
                <Badge>{selectedCourse.category}</Badge>
                <Badge>{selectedCourse.is_published ? 'Published' : 'Draft'}</Badge>
              </div>
            </div>
            <div className="flex gap-2">
              {!selectedCourse.is_published && <Button className="btn-sketchy" onClick={() => handlePublish(selectedCourse.id)}><Eye className="w-4 h-4 mr-2" />Publish</Button>}
              <Button variant="outline" className="border-2 border-black" onClick={() => { setForm(selectedCourse); setView('form'); }}><Edit className="w-4 h-4" /></Button>
              <Button variant="outline" className="border-2 border-red-500 text-red-500" onClick={() => handleDeleteCourse(selectedCourse.id)}><Trash2 className="w-4 h-4" /></Button>
              <Button variant="outline" className="border-2 border-black" onClick={() => { setSelectedCourse(null); setView('list'); }}>Back</Button>
            </div>
          </div>

          <div className="card-sketchy">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-sketch text-xl">Modules ({modules.length})</h2>
              <Button className="btn-sketchy" onClick={() => { setModuleForm({ title: '', description: '', order: modules.length + 1, video_url: '', content: '', duration_minutes: 0 }); setView('module-form'); }}><Plus className="w-4 h-4 mr-2" />Add</Button>
            </div>
            {modules.length > 0 ? modules.map((m, idx) => (
              <div key={m.id} className="p-4 border-2 border-black rounded-lg mb-2 flex justify-between items-center">
                <div>
                  <p className="font-bold">{idx + 1}. {m.title}</p>
                  <p className="text-sm text-muted-foreground">{m.description}</p>
                  {m.quiz_id && <Badge className="mt-1">Has Quiz</Badge>}
                </div>
                {!m.quiz_id && <Button size="sm" onClick={() => { setSelectedModule(m); setView('quiz-form'); }}><Plus className="w-4 h-4 mr-1" />Quiz</Button>}
              </div>
            )) : <p className="text-muted-foreground text-center py-4">No modules</p>}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-sketch text-4xl">Manage Courses</h1>
            <p className="text-muted-foreground">Create and manage content</p>
          </div>
          <Button className="btn-sketchy" onClick={() => { setForm({ title: '', description: '', thumbnail_url: '', category: 'Technology', difficulty: 'beginner', duration_hours: 1 }); setSelectedCourse(null); setView('form'); }}><Plus className="w-4 h-4 mr-2" />New Course</Button>
        </div>

        {courses.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map(c => (
              <div key={c.id} onClick={() => selectCourse(c)} className="card-sketchy cursor-pointer hover:-translate-y-1 transition-transform">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-lg">{c.title}</h3>
                  {c.is_published ? <Eye className="w-5 h-5 text-green-500" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{c.description}</p>
                <div className="flex gap-2 mt-3">
                  <Badge variant="outline">{c.category}</Badge>
                  <Badge variant="outline">{c.difficulty}</Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-sketchy text-center py-16">
            <BookOpen className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-sketch text-2xl">No courses</h3>
            <p className="text-muted-foreground">Create your first course!</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
